<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { fetchAdminDiagnostics, type AdminDiagnosticsRecord } from '@/api/endpoints'
import { useAutoUpdate } from '@/composables/useAutoUpdate'
import { useTheme } from '@/composables/useTheme'
import { useAuthStore } from '@/stores/auth'
import { useDashboardStore } from '@/stores/dashboard'
import { useMeterScanQueueStore } from '@/stores/meterScanQueue'
import { useSettingsStore } from '@/stores/settings'
import { LogOut, Menu, X, Home, Settings, FileText, Building2, MapPin, ChevronRight, Boxes, Warehouse, FolderTree, Printer, TriangleAlert, Server, ListTodo, Plane, UserCircle, Sun, Moon, MonitorSmartphone } from 'lucide-vue-next'

const router = useRouter()
const route = useRoute()
const auth = useAuthStore()
const dashboard = useDashboardStore()
const settingsStore = useSettingsStore()
const meterScanQueue = useMeterScanQueueStore()
const { preference: themePreference, cycleTheme } = useTheme()
const mobileMenuOpen = ref(false)

const themeIcon = computed(() => {
  if (themePreference.value === 'light') return Sun
  if (themePreference.value === 'dark') return Moon
  return MonitorSmartphone
})

const themeTitle = computed(() => {
  if (themePreference.value === 'light') return 'Design: Hell (klicken für Dunkel)'
  if (themePreference.value === 'dark') return 'Design: Dunkel (klicken für System)'
  return 'Design: System (klicken für Hell)'
})
const adminDiagnostics = ref<AdminDiagnosticsRecord | null>(null)
const adminDiagnosticsLoading = ref(false)
const adminDiagnosticsRequestFailed = ref(false)

const isAdmin = computed(() => auth.isAdmin)

const locations = computed(() =>
  dashboard.locations.filter((loc) =>
    dashboard.groups.some((g) => {
      const locId = g.location?.['@id']?.match(/\/(\d+)$/)?.[1]
      return locId && Number(locId) === loc.id
    }),
  ),
)
const activeLocationId = computed(() => dashboard.activeLocationId)
const locationGroups = computed(() => dashboard.locationGroups)
const activeGroupId = computed(() => dashboard.activeGroupId)
const isDashboard = computed(() => route.name === 'dashboard')
const isServerRoute = computed(() => route.path.startsWith('/server'))
const isServerOverviewRoute = computed(() => route.path === '/server/overview')
const isServerSystemRoute = computed(() => route.path === '/server/system')
const isServerDockerRoute = computed(() => route.path === '/server/docker')
const isServerBackupRoute = computed(() => route.path === '/server/backup')
const isInventoryRoute = computed(() => route.path.startsWith('/inventory'))
const isInventoryLocationsRoute = computed(() => route.name === 'inventory-locations')
const isInventoryCategoriesRoute = computed(() => route.name === 'inventory-categories')
const isInventoryLabelsRoute = computed(() => route.name === 'inventory-labels')
const isInventoryItemsRoute = computed(() => route.name === 'inventory' || route.name === 'inventory-detail')
const showDashboardSubMenus = computed(() => route.name === 'dashboard' || route.name === 'detail')
const activeLocationCity = computed(() => dashboard.activeLocationCity)
const headerSafeAreaStyle = { paddingTop: 'env(safe-area-inset-top, 0px)' }
const adminDiagnosticsDisplayStatus = computed<'ok' | 'warning' | 'error'>(() => {
  if (adminDiagnosticsRequestFailed.value) return 'warning'
  if (!adminDiagnostics.value) return 'ok'
  if (adminDiagnostics.value.stale && adminDiagnostics.value.status === 'ok') return 'warning'
  return adminDiagnostics.value.status
})
const showAdminDiagnosticsWarning = computed(() => isAdmin.value && adminDiagnosticsDisplayStatus.value !== 'ok')
const adminDiagnosticsTitle = computed(() => {
  if (adminDiagnosticsRequestFailed.value) return 'Konfigurationsstatus konnte nicht geladen werden'
  if (adminDiagnostics.value?.stale) return 'Diagnose-Cache ist veraltet'
  if (adminDiagnosticsDisplayStatus.value === 'error') return 'Konfigurationsfehler vorhanden'
  if (adminDiagnosticsDisplayStatus.value === 'warning') return 'Konfigurationswarnung vorhanden'
  return 'Konfiguration plausibel'
})
const adminDiagnosticsLinkClass = computed(() => adminDiagnosticsDisplayStatus.value === 'error'
  ? 'text-sh-red hover:text-sh-red border-sh-red/30 bg-sh-red/10 hover:bg-sh-red/15'
  : 'text-sh-amber hover:text-sh-amber border-sh-amber/30 bg-sh-amber/10 hover:bg-sh-amber/15')

async function loadAdminDiagnosticsStatus() {
  if (!isAdmin.value || adminDiagnosticsLoading.value) return

  adminDiagnosticsLoading.value = true

  try {
    adminDiagnostics.value = await fetchAdminDiagnostics()
    adminDiagnosticsRequestFailed.value = false
  } catch {
    adminDiagnosticsRequestFailed.value = true
  } finally {
    adminDiagnosticsLoading.value = false
  }
}

/** Wie viele ausgewertete Zählerfotos auf Prüfung warten (Badge am Hausverwaltungs-Link). */
const meterScanReviewCount = computed(() => meterScanQueue.reviewCount)
const meterScanBadgeLabel = computed(() => meterScanReviewCount.value > 99 ? '99+' : String(meterScanReviewCount.value))
const meterScanBadgeTitle = computed(() => meterScanReviewCount.value === 1
  ? '1 Zählerstand wartet auf Prüfung'
  : `${meterScanReviewCount.value} Zählerstände warten auf Prüfung`)

async function refreshHeaderStatus() {
  await Promise.all([loadAdminDiagnosticsStatus(), meterScanQueue.refresh()])
}

onMounted(() => {
  void refreshHeaderStatus()
})

// Nach dem Login ist der Header schon montiert — sonst bliebe der Badge bis zum nächsten Poll leer.
watch(() => auth.isAuthenticated, (authenticated) => {
  if (authenticated) void refreshHeaderStatus()
})

useAutoUpdate(refreshHeaderStatus, 60_000)

function selectLocation(id: number) {
  dashboard.switchLocation(id)
  const firstGroup = dashboard.locationGroups[0]
  if (firstGroup) {
    router.push(`/dashboard/${firstGroup.id}`)
  } else {
    router.push('/dashboard')
  }
  mobileMenuOpen.value = false
}

function selectGroup(groupId: number) {
  dashboard.switchGroup(groupId)
  router.push(`/dashboard/${groupId}`)
  mobileMenuOpen.value = false
}

async function handleLogout() {
  await auth.logout()
  router.push('/login')
}

function navigateTo(to: string) {
  router.push(to)
  mobileMenuOpen.value = false
}

watch(() => route.fullPath, () => {
  mobileMenuOpen.value = false
})
</script>

<template>
  <header class="fixed top-0 left-0 right-0 z-[100]" :style="headerSafeAreaStyle">
    <!-- Level 1: Main navigation -->
    <nav class="h-[3em] bg-sh-menu border-b border-sh-glass-border flex items-center px-[0.7em] shadow-[0_10px_28px_rgba(60,92,111,0.12)] backdrop-blur-md">
      <!-- Desktop nav (left side) -->
      <div class="hidden sm:flex items-center gap-[0.4em] flex-1">
        <!-- Dashboard (always visible) -->
        <router-link
          to="/dashboard"
          data-testid="nav-dashboard"
          class="px-[0.55em] py-[0.34em] font-semibold no-underline rounded-[0.5em] transition-all duration-200 text-[0.9em] border"
          :class="isDashboard
            ? 'bg-sh-accent/10 text-sh-accent border-[color:var(--color-sh-accent-border)]'
            : 'text-sh-text border-transparent hover:bg-sh-menu-hover hover:border-sh-glass-border'"
        >
          <Home :size="14" class="inline-block mr-[0.2em] -mt-[0.1em]" />
          Dashboard
        </router-link>

        <!-- Separator -->
        <div class="w-px h-[1.5em] bg-sh-accent/15 mx-[0.2em]" />

        <!-- Documents link -->
        <router-link
          v-if="auth.canAccessApp('documents')"
          to="/documents"
          data-testid="nav-documents"
          class="px-[0.55em] py-[0.34em] text-sh-text-muted font-medium no-underline hover:bg-sh-menu-hover hover:text-sh-text rounded-[0.5em] border border-transparent hover:border-sh-glass-border transition-all duration-200 text-[0.85em]"
          :class="{ '!bg-sh-accent/10 !text-sh-accent !border-[color:var(--color-sh-accent-border)]': route.path.startsWith('/documents') }"
        >
          <FileText :size="14" class="inline-block mr-[0.2em] -mt-[0.1em]" />
          Dokumente
        </router-link>

        <router-link
          v-if="auth.canAccessApp('inventory')"
          to="/inventory"
          data-testid="nav-inventory"
          class="px-[0.55em] py-[0.34em] text-sh-text-muted font-medium no-underline hover:bg-sh-menu-hover hover:text-sh-text rounded-[0.5em] border border-transparent hover:border-sh-glass-border transition-all duration-200 text-[0.85em]"
          :class="{ '!bg-sh-accent/10 !text-sh-accent !border-[color:var(--color-sh-accent-border)]': route.path.startsWith('/inventory') }"
        >
          <Boxes :size="14" class="inline-block mr-[0.2em] -mt-[0.1em]" />
          Inventar
        </router-link>

        <!-- Hausverwaltung link -->
        <router-link
          v-if="auth.canAccessApp('hausverwaltung')"
          to="/hausverwaltung"
          data-testid="nav-hausverwaltung"
          class="px-[0.55em] py-[0.34em] text-sh-text-muted font-medium no-underline hover:bg-sh-menu-hover hover:text-sh-text rounded-[0.5em] border border-transparent hover:border-sh-glass-border transition-all duration-200 text-[0.85em]"
          :class="{ '!bg-sh-accent/10 !text-sh-accent !border-[color:var(--color-sh-accent-border)]': route.path.startsWith('/hausverwaltung') }"
        >
          <Building2 :size="14" class="inline-block mr-[0.2em] -mt-[0.1em]" />
          Hausverwaltung
          <span
            v-if="meterScanReviewCount > 0"
            data-testid="nav-meter-scan-badge"
            :title="meterScanBadgeTitle"
            class="inline-block ml-[0.35em] px-[0.4em] py-[0.05em] rounded-full bg-sh-accent text-sh-bg text-[0.75em] font-bold leading-[1.4]"
          >{{ meterScanBadgeLabel }}</span>
        </router-link>

        <!-- Aufgaben link -->
        <router-link
          v-if="auth.canAccessApp('todos')"
          to="/todos"
          data-testid="nav-todos"
          class="px-[0.55em] py-[0.34em] text-sh-text-muted font-medium no-underline hover:bg-sh-menu-hover hover:text-sh-text rounded-[0.5em] border border-transparent hover:border-sh-glass-border transition-all duration-200 text-[0.85em]"
          :class="{ '!bg-sh-accent/10 !text-sh-accent !border-[color:var(--color-sh-accent-border)]': route.path.startsWith('/todos') }"
        >
          <ListTodo :size="14" class="inline-block mr-[0.2em] -mt-[0.1em]" />
          Aufgaben
        </router-link>

        <!-- Flugverbindungen link -->
        <router-link
          v-if="auth.canAccessApp('flights')"
          to="/flights"
          data-testid="nav-flights"
          class="px-[0.55em] py-[0.34em] text-sh-text-muted font-medium no-underline hover:bg-sh-menu-hover hover:text-sh-text rounded-[0.5em] border border-transparent hover:border-sh-glass-border transition-all duration-200 text-[0.85em]"
          :class="{ '!bg-sh-accent/10 !text-sh-accent !border-[color:var(--color-sh-accent-border)]': route.path.startsWith('/flights') }"
        >
          <Plane :size="14" class="inline-block mr-[0.2em] -mt-[0.1em]" />
          Flugverbindungen
        </router-link>

        <router-link
          v-if="isAdmin"
          to="/server/overview"
          data-testid="nav-server"
          class="px-[0.55em] py-[0.34em] text-sh-text-muted font-medium no-underline hover:bg-sh-menu-hover hover:text-sh-text rounded-[0.5em] border border-transparent hover:border-sh-glass-border transition-all duration-200 text-[0.85em]"
          :class="{ '!bg-sh-accent/10 !text-sh-accent !border-[color:var(--color-sh-accent-border)]': isServerRoute }"
        >
          <Server :size="14" class="inline-block mr-[0.2em] -mt-[0.1em]" />
          Server
        </router-link>
      </div>

      <!-- Mobile: Burger + Logo -->
      <div class="flex sm:hidden items-center flex-1">
        <button
          class="w-[2.2em] h-[2.2em] flex items-center justify-center hover:bg-sh-menu-hover rounded-[0.5em] border border-transparent hover:border-sh-glass-border transition-colors"
          @click="mobileMenuOpen = !mobileMenuOpen"
        >
          <component :is="mobileMenuOpen ? X : Menu" :size="20" class="text-sh-text" />
        </button>
        <span class="ml-[0.55em] text-sh-text font-semibold text-[0.95em] tracking-[0.02em]">{{ settingsStore.dashboardTitle }}</span>
      </div>

      <!-- Right side: Admin + Logout -->
      <div class="flex items-center gap-[0.3em]">
        <router-link
          v-if="showAdminDiagnosticsWarning"
          to="/admin/configurationProblems"
          class="hidden sm:flex w-[2.2em] h-[2.2em] items-center justify-center rounded-[0.5em] border transition-colors"
          :class="adminDiagnosticsLinkClass"
          :title="adminDiagnosticsTitle"
        >
          <TriangleAlert :size="17" />
        </router-link>

        <!-- Theme-Umschalter: hell → dunkel → System -->
        <button
          class="w-[2.2em] h-[2.2em] flex items-center justify-center hover:bg-sh-menu-hover rounded-[0.5em] border border-transparent hover:border-sh-glass-border transition-colors"
          :title="themeTitle"
          :aria-label="themeTitle"
          data-testid="nav-theme-toggle"
          @click="cycleTheme"
        >
          <component :is="themeIcon" :size="17" class="text-sh-text-muted" />
        </button>

        <!-- Einstellungen link (right side, initials/profile icon) -->
        <router-link
          to="/einstellungen"
          data-testid="nav-settings"
          class="hidden sm:flex w-[2.2em] h-[2.2em] items-center justify-center hover:bg-sh-menu-hover rounded-[0.5em] border border-transparent hover:border-sh-glass-border transition-colors"
          :class="{ '!bg-sh-accent/10 !text-sh-accent !border-[color:var(--color-sh-accent-border)]': route.path === '/einstellungen' }"
          title="Einstellungen"
        >
          <UserCircle :size="18" class="text-sh-text-muted" />
        </router-link>

        <!-- Admin link (right side, gear icon) -->
        <router-link
          v-if="isAdmin"
          to="/admin/devices"
          data-testid="nav-admin"
          class="hidden sm:flex items-center px-[0.55em] py-[0.34em] text-sh-text-muted font-medium no-underline hover:bg-sh-menu-hover hover:text-sh-text rounded-[0.5em] border border-transparent hover:border-sh-glass-border transition-all duration-200 text-[0.85em]"
          :class="{ '!bg-sh-accent/10 !text-sh-accent !border-[color:var(--color-sh-accent-border)]': route.path.startsWith('/admin') }"
          title="Administration"
        >
          <Settings :size="15" class="mr-[0.2em]" />
          Admin
        </router-link>

        <!-- Logout -->
        <button
          class="w-[2.2em] h-[2.2em] flex items-center justify-center hover:bg-sh-menu-hover rounded-[0.5em] border border-transparent hover:border-sh-glass-border transition-colors"
          title="Abmelden"
          data-testid="nav-logout"
          @click="handleLogout"
        >
          <LogOut :size="18" class="text-sh-text-muted" />
        </button>
      </div>
    </nav>

    <!-- Level 2: Inventory submenu -->
    <nav v-if="isInventoryRoute" class="h-[2.5em] bg-sh-submenu border-b border-sh-glass-border hidden sm:flex items-center px-[0.7em] gap-[0.35em] backdrop-blur-md">
      <Boxes :size="13" class="text-sh-text-muted mr-[0.1em]" />
      <router-link
        to="/inventory"
        class="px-[0.55em] py-[0.28em] font-semibold rounded-[0.5em] transition-all duration-200 text-[0.85em] border no-underline"
        :class="isInventoryItemsRoute
          ? 'bg-sh-accent/10 text-sh-accent border-[color:var(--color-sh-accent-border)]'
          : 'text-sh-text-muted hover:bg-sh-submenu-hover border-transparent hover:border-sh-glass-border'"
      >
        Inventar
      </router-link>
      <router-link
        to="/inventory/locations"
        class="px-[0.55em] py-[0.28em] font-semibold rounded-[0.5em] transition-all duration-200 text-[0.85em] border no-underline"
        :class="isInventoryLocationsRoute
          ? 'bg-sh-accent/10 text-sh-accent border-[color:var(--color-sh-accent-border)]'
          : 'text-sh-text-muted hover:bg-sh-submenu-hover border-transparent hover:border-sh-glass-border'"
      >
        Lagerorte
      </router-link>
      <router-link
        v-if="auth.canAccessApp('inventory')"
        to="/inventory/categories"
        class="px-[0.55em] py-[0.28em] font-semibold rounded-[0.5em] transition-all duration-200 text-[0.85em] border no-underline"
        :class="isInventoryCategoriesRoute
          ? 'bg-sh-accent/10 text-sh-accent border-[color:var(--color-sh-accent-border)]'
          : 'text-sh-text-muted hover:bg-sh-submenu-hover border-transparent hover:border-sh-glass-border'"
      >
        Kategorien
      </router-link>
      <router-link
        v-if="auth.canAccessApp('inventory')"
        to="/inventory/labels"
        class="px-[0.55em] py-[0.28em] font-semibold rounded-[0.5em] transition-all duration-200 text-[0.85em] border no-underline"
        :class="isInventoryLabelsRoute
          ? 'bg-sh-accent/10 text-sh-accent border-[color:var(--color-sh-accent-border)]'
          : 'text-sh-text-muted hover:bg-sh-submenu-hover border-transparent hover:border-sh-glass-border'"
      >
        Etiketten
      </router-link>
    </nav>

    <nav v-if="isServerRoute" class="h-[2.5em] bg-sh-submenu border-b border-sh-glass-border hidden sm:flex items-center px-[0.7em] gap-[0.35em] backdrop-blur-md">
      <Server :size="13" class="text-sh-text-muted mr-[0.1em]" />
      <router-link
        to="/server/overview"
        data-testid="server-section-overview"
        class="px-[0.55em] py-[0.28em] font-semibold rounded-[0.5em] transition-all duration-200 text-[0.85em] border no-underline"
        :class="isServerOverviewRoute
          ? 'bg-sh-accent/10 text-sh-accent border-[color:var(--color-sh-accent-border)]'
          : 'text-sh-text-muted hover:bg-sh-submenu-hover border-transparent hover:border-sh-glass-border'"
      >
        Überblick
      </router-link>
      <router-link
        to="/server/system"
        data-testid="server-section-system"
        class="px-[0.55em] py-[0.28em] font-semibold rounded-[0.5em] transition-all duration-200 text-[0.85em] border no-underline"
        :class="isServerSystemRoute
          ? 'bg-sh-accent/10 text-sh-accent border-[color:var(--color-sh-accent-border)]'
          : 'text-sh-text-muted hover:bg-sh-submenu-hover border-transparent hover:border-sh-glass-border'"
      >
        System
      </router-link>
      <router-link
        to="/server/docker"
        data-testid="server-section-docker"
        class="px-[0.55em] py-[0.28em] font-semibold rounded-[0.5em] transition-all duration-200 text-[0.85em] border no-underline"
        :class="isServerDockerRoute
          ? 'bg-sh-accent/10 text-sh-accent border-[color:var(--color-sh-accent-border)]'
          : 'text-sh-text-muted hover:bg-sh-submenu-hover border-transparent hover:border-sh-glass-border'"
      >
        Docker
      </router-link>
      <router-link
        to="/server/backup"
        data-testid="server-section-backup"
        class="px-[0.55em] py-[0.28em] font-semibold rounded-[0.5em] transition-all duration-200 text-[0.85em] border no-underline"
        :class="isServerBackupRoute
          ? 'bg-sh-accent/10 text-sh-accent border-[color:var(--color-sh-accent-border)]'
          : 'text-sh-text-muted hover:bg-sh-submenu-hover border-transparent hover:border-sh-glass-border'"
      >
        Backup
      </router-link>
    </nav>

    <!-- Level 2: Location tabs (dashboard + detail) -->
    <nav v-if="showDashboardSubMenus && locations.length" class="h-[2.5em] bg-sh-submenu border-b border-sh-glass-border hidden sm:flex items-center px-[0.7em] gap-[0.35em] backdrop-blur-md">
      <MapPin :size="13" class="text-sh-text-muted mr-[0.1em]" />
      <button
        v-for="loc in locations"
        :key="loc.id"
        class="px-[0.55em] py-[0.28em] font-semibold rounded-[0.5em] transition-all duration-200 text-[0.85em] border"
        :class="loc.id === activeLocationId
          ? 'bg-sh-accent/10 text-sh-accent border-[color:var(--color-sh-accent-border)]'
          : 'text-sh-text-muted hover:bg-sh-submenu-hover border-transparent hover:border-sh-glass-border'"
        @click="selectLocation(loc.id)"
      >
        {{ loc.city }}
      </button>
    </nav>

    <!-- Level 3: Group tabs (dashboard + detail with active location) -->
    <nav v-if="showDashboardSubMenus && locationGroups.length" class="h-[2.5em] bg-sh-submenu/80 border-b border-sh-glass-border hidden sm:flex items-center px-[0.7em] gap-[0.35em] backdrop-blur-md">
      <span v-if="activeLocationCity" class="text-sh-text-muted text-[0.75em] mr-[0.2em] flex items-center gap-[0.15em]">
        {{ activeLocationCity }}
        <ChevronRight :size="11" />
      </span>
      <button
        v-for="group in locationGroups"
        :key="group.id"
        class="px-[0.55em] py-[0.28em] text-sh-text font-medium rounded-[0.5em] transition-all duration-200 text-[0.85em] border"
        :class="group.id === activeGroupId
          ? 'bg-sh-accent/10 text-sh-accent border-[color:var(--color-sh-accent-border)]'
          : 'hover:bg-sh-submenu-hover text-sh-text-muted border-transparent hover:border-sh-glass-border'"
        @click="selectGroup(group.id)"
      >
        {{ group.name }}
      </button>
    </nav>

    <!-- Mobile drawer -->
    <Transition name="slide">
      <div v-if="mobileMenuOpen" class="sm:hidden bg-sh-dark-ee border-b border-sh-glass-border px-[0.7em] py-[0.6em] flex flex-col gap-[0.35em] backdrop-blur-md">
        <!-- Dashboard -->
        <button
          class="px-[0.55em] py-[0.45em] text-left font-semibold rounded-[0.5em] transition-colors text-[0.9em] border"
          :class="isDashboard
            ? 'bg-sh-accent/10 text-sh-accent border-[color:var(--color-sh-accent-border)]'
            : 'text-sh-text hover:bg-sh-menu-hover border-transparent hover:border-sh-glass-border'"
          @click="navigateTo('/dashboard')"
        >
          <Home :size="14" class="inline-block mr-[0.3em] -mt-[0.1em]" />
          Dashboard
        </button>

        <button
          v-if="isAdmin"
          class="px-[0.55em] py-[0.45em] text-left font-semibold rounded-[0.5em] transition-colors text-[0.9em] border"
          :class="isServerRoute
            ? 'bg-sh-accent/10 text-sh-accent border-[color:var(--color-sh-accent-border)]'
            : 'text-sh-text hover:bg-sh-menu-hover border-transparent hover:border-sh-glass-border'"
          @click="navigateTo('/server/overview')"
        >
          <Server :size="14" class="inline-block mr-[0.3em] -mt-[0.1em]" />
          Server
        </button>

        <template v-if="isServerRoute">
          <button
            class="ml-[1em] px-[0.55em] py-[0.35em] text-left font-medium rounded-[0.5em] transition-colors text-[0.85em] border"
            :class="isServerOverviewRoute
              ? 'bg-sh-accent/10 text-sh-accent border-[color:var(--color-sh-accent-border)]'
              : 'text-sh-text-muted hover:bg-sh-menu-hover border-transparent hover:border-sh-glass-border'"
            @click="navigateTo('/server/overview')"
          >
            Überblick
          </button>
          <button
            class="ml-[1em] px-[0.55em] py-[0.35em] text-left font-medium rounded-[0.5em] transition-colors text-[0.85em] border"
            :class="isServerSystemRoute
              ? 'bg-sh-accent/10 text-sh-accent border-[color:var(--color-sh-accent-border)]'
              : 'text-sh-text-muted hover:bg-sh-menu-hover border-transparent hover:border-sh-glass-border'"
            @click="navigateTo('/server/system')"
          >
            System
          </button>
          <button
            class="ml-[1em] px-[0.55em] py-[0.35em] text-left font-medium rounded-[0.5em] transition-colors text-[0.85em] border"
            :class="isServerDockerRoute
              ? 'bg-sh-accent/10 text-sh-accent border-[color:var(--color-sh-accent-border)]'
              : 'text-sh-text-muted hover:bg-sh-menu-hover border-transparent hover:border-sh-glass-border'"
            @click="navigateTo('/server/docker')"
          >
            Docker
          </button>
          <button
            class="ml-[1em] px-[0.55em] py-[0.35em] text-left font-medium rounded-[0.5em] transition-colors text-[0.85em] border"
            :class="isServerBackupRoute
              ? 'bg-sh-accent/10 text-sh-accent border-[color:var(--color-sh-accent-border)]'
              : 'text-sh-text-muted hover:bg-sh-menu-hover border-transparent hover:border-sh-glass-border'"
            @click="navigateTo('/server/backup')"
          >
            Backup
          </button>
        </template>

        <button
          v-if="auth.canAccessApp('inventory')"
          class="px-[0.55em] py-[0.45em] text-left font-semibold rounded-[0.5em] transition-colors text-[0.9em] border"
          :class="route.path.startsWith('/inventory')
            ? 'bg-sh-accent/10 text-sh-accent border-[color:var(--color-sh-accent-border)]'
            : 'text-sh-text hover:bg-sh-menu-hover border-transparent hover:border-sh-glass-border'"
          @click="navigateTo('/inventory')"
        >
          <Boxes :size="14" class="inline-block mr-[0.3em] -mt-[0.1em]" />
          Inventar
        </button>

        <template v-if="isInventoryRoute">
          <button
            class="ml-[1em] px-[0.55em] py-[0.35em] text-left font-medium rounded-[0.5em] transition-colors text-[0.85em] border"
            :class="isInventoryItemsRoute
              ? 'bg-sh-accent/10 text-sh-accent border-[color:var(--color-sh-accent-border)]'
              : 'text-sh-text-muted hover:bg-sh-menu-hover border-transparent hover:border-sh-glass-border'"
            @click="navigateTo('/inventory')"
          >
            <Boxes :size="12" class="inline-block mr-[0.2em] -mt-[0.1em]" />
            Inventar
          </button>
          <button
            class="ml-[1em] px-[0.55em] py-[0.35em] text-left font-medium rounded-[0.5em] transition-colors text-[0.85em] border"
            :class="isInventoryLocationsRoute
              ? 'bg-sh-accent/10 text-sh-accent border-[color:var(--color-sh-accent-border)]'
              : 'text-sh-text-muted hover:bg-sh-menu-hover border-transparent hover:border-sh-glass-border'"
            @click="navigateTo('/inventory/locations')"
          >
            <Warehouse :size="12" class="inline-block mr-[0.2em] -mt-[0.1em]" />
            Lagerorte
          </button>
          <button
            v-if="auth.canAccessApp('inventory')"
            class="ml-[1em] px-[0.55em] py-[0.35em] text-left font-medium rounded-[0.5em] transition-colors text-[0.85em] border"
            :class="isInventoryCategoriesRoute
              ? 'bg-sh-accent/10 text-sh-accent border-[color:var(--color-sh-accent-border)]'
              : 'text-sh-text-muted hover:bg-sh-menu-hover border-transparent hover:border-sh-glass-border'"
            @click="navigateTo('/inventory/categories')"
          >
            <FolderTree :size="12" class="inline-block mr-[0.2em] -mt-[0.1em]" />
            Kategorien
          </button>
          <button
            v-if="auth.canAccessApp('inventory')"
            class="ml-[1em] px-[0.55em] py-[0.35em] text-left font-medium rounded-[0.5em] transition-colors text-[0.85em] border"
            :class="isInventoryLabelsRoute
              ? 'bg-sh-accent/10 text-sh-accent border-[color:var(--color-sh-accent-border)]'
              : 'text-sh-text-muted hover:bg-sh-menu-hover border-transparent hover:border-sh-glass-border'"
            @click="navigateTo('/inventory/labels')"
          >
            <Printer :size="12" class="inline-block mr-[0.2em] -mt-[0.1em]" />
            Etiketten
          </button>
        </template>

        <button
          v-if="auth.canAccessApp('documents')"
          class="px-[0.55em] py-[0.45em] text-left font-semibold rounded-[0.5em] transition-colors text-[0.9em] border"
          :class="route.path.startsWith('/documents')
            ? 'bg-sh-accent/10 text-sh-accent border-[color:var(--color-sh-accent-border)]'
            : 'text-sh-text hover:bg-sh-menu-hover border-transparent hover:border-sh-glass-border'"
          @click="navigateTo('/documents')"
        >
          <FileText :size="14" class="inline-block mr-[0.3em] -mt-[0.1em]" />
          Dokumente
        </button>

        <button
          v-if="auth.canAccessApp('hausverwaltung')"
          data-testid="nav-hausverwaltung-mobile"
          class="px-[0.55em] py-[0.45em] text-left font-semibold rounded-[0.5em] transition-colors text-[0.9em] border"
          :class="route.path.startsWith('/hausverwaltung')
            ? 'bg-sh-accent/10 text-sh-accent border-[color:var(--color-sh-accent-border)]'
            : 'text-sh-text hover:bg-sh-menu-hover border-transparent hover:border-sh-glass-border'"
          @click="navigateTo('/hausverwaltung')"
        >
          <Building2 :size="14" class="inline-block mr-[0.3em] -mt-[0.1em]" />
          Hausverwaltung
          <span
            v-if="meterScanReviewCount > 0"
            data-testid="nav-meter-scan-badge-mobile"
            :title="meterScanBadgeTitle"
            class="inline-block ml-[0.35em] px-[0.4em] py-[0.05em] rounded-full bg-sh-accent text-sh-bg text-[0.75em] font-bold leading-[1.4]"
          >{{ meterScanBadgeLabel }}</span>
        </button>

        <button
          v-if="auth.canAccessApp('todos')"
          data-testid="nav-todos-mobile"
          class="px-[0.55em] py-[0.45em] text-left font-semibold rounded-[0.5em] transition-colors text-[0.9em] border"
          :class="route.path.startsWith('/todos')
            ? 'bg-sh-accent/10 text-sh-accent border-[color:var(--color-sh-accent-border)]'
            : 'text-sh-text hover:bg-sh-menu-hover border-transparent hover:border-sh-glass-border'"
          @click="navigateTo('/todos')"
        >
          <ListTodo :size="14" class="inline-block mr-[0.3em] -mt-[0.1em]" />
          Aufgaben
        </button>

        <button
          v-if="auth.canAccessApp('flights')"
          data-testid="nav-flights-mobile"
          class="px-[0.55em] py-[0.45em] text-left font-semibold rounded-[0.5em] transition-colors text-[0.9em] border"
          :class="route.path.startsWith('/flights')
            ? 'bg-sh-accent/10 text-sh-accent border-[color:var(--color-sh-accent-border)]'
            : 'text-sh-text hover:bg-sh-menu-hover border-transparent hover:border-sh-glass-border'"
          @click="navigateTo('/flights')"
        >
          <Plane :size="14" class="inline-block mr-[0.3em] -mt-[0.1em]" />
          Flugverbindungen
        </button>

        <!-- Location tabs (indented under dashboard) -->
        <template v-if="locations.length">
          <template v-for="loc in locations" :key="loc.id">
            <button
              class="ml-[1em] px-[0.55em] py-[0.35em] text-left font-medium rounded-[0.5em] transition-colors text-[0.85em] border"
              :class="loc.id === activeLocationId && isDashboard
                ? 'bg-sh-accent/10 text-sh-accent border-[color:var(--color-sh-accent-border)]'
                : 'text-sh-text-muted hover:bg-sh-menu-hover border-transparent hover:border-sh-glass-border'"
              @click="selectLocation(loc.id)"
            >
              <MapPin :size="12" class="inline-block mr-[0.2em] -mt-[0.1em]" />
              {{ loc.city }}
            </button>
            <!-- Group tabs under active location -->
            <template v-if="loc.id === activeLocationId && isDashboard && locationGroups.length">
              <button
                v-for="group in locationGroups"
                :key="group.id"
                class="ml-[2em] px-[0.55em] py-[0.3em] text-left font-medium rounded-[0.5em] transition-colors text-[0.8em] border"
                :class="group.id === activeGroupId
                  ? 'bg-sh-accent/10 text-sh-accent border-[color:var(--color-sh-accent-border)]'
                  : 'text-sh-text-muted hover:bg-sh-menu-hover border-transparent hover:border-sh-glass-border'"
                @click="selectGroup(group.id)"
              >
                {{ group.name }}
              </button>
            </template>
          </template>
        </template>

        <div class="h-px bg-sh-accent/15 my-[0.2em]" />

        <!-- Einstellungen (bottom, with separator) -->
        <button
          data-testid="nav-settings-mobile"
          class="px-[0.55em] py-[0.45em] text-left text-sh-text-muted font-medium rounded-[0.5em] hover:bg-sh-menu-hover hover:text-sh-text border border-transparent hover:border-sh-glass-border transition-colors text-[0.85em]"
          @click="navigateTo('/einstellungen')"
        >
          <UserCircle :size="14" class="inline-block mr-[0.3em] -mt-[0.1em]" />
          Einstellungen
        </button>

        <!-- Admin (bottom, with separator) -->
        <template v-if="isAdmin">
          <div class="h-px bg-sh-accent/15 my-[0.2em]" />
          <button
            class="px-[0.55em] py-[0.45em] text-left text-sh-text-muted font-medium rounded-[0.5em] hover:bg-sh-menu-hover hover:text-sh-text border border-transparent hover:border-sh-glass-border transition-colors text-[0.85em]"
            @click="navigateTo('/admin/devices')"
          >
            <Settings :size="14" class="inline-block mr-[0.3em] -mt-[0.1em]" />
            Admin
          </button>
        </template>
      </div>
    </Transition>
  </header>
</template>

<style scoped>
.slide-enter-active,
.slide-leave-active {
  transition: all 0.2s ease;
}

.slide-enter-from,
.slide-leave-to {
  opacity: 0;
  transform: translateY(-0.5em);
}
</style>
